import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { blank, Guid, Nillable, valueOrDefault } from '@web/utils';
import {
  OrderStatusPipe,
  PublicCheckoutOrderDetails,
  PublicOrderDataSourceService,
} from '@web/shared/module/shop';
import {
  CurrencyComponent,
  SlimButtonComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Route, RouterLinkComponent } from '@web/common';
import { interval, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-public-order-summary',
  imports: [
    CommonModule,
    CurrencyComponent,
    SpinnerFilledComponent,
    RouterLinkComponent,
    SlimButtonComponent,
    OrderStatusPipe,
  ],
  templateUrl: './public-order-summary.component.html',
  styleUrl: './public-order-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicOrderSummaryComponent implements OnInit {
  private readonly orderDataSource = inject(PublicOrderDataSourceService);

  public readonly accessToken = input.required<string>();
  public readonly orderId = input.required<Guid>();

  protected readonly data = signal<Nillable<PublicCheckoutOrderDetails>>(null);
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly Route = Route;

  private readonly timerStop$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.fetchCheckoutOrderDetails().subscribe(() => this.startInterval());
  }

  private startInterval(): void {
    if (!this.data()?.isWaitingForPayment()) {
      return;
    }

    interval(5000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.timerStop$),
        switchMap(() => this.fetchCheckoutOrderDetails()),
      )
      .subscribe(() => {
        if (!this.data()?.isWaitingForPayment()) {
          this.timerStop$.next();
        }
      });
  }

  private fetchCheckoutOrderDetails(): Observable<void> {
    return this.orderDataSource
      .fetchCheckoutOrderDetailsById({
        accessToken: this.accessToken(),
        orderId: this.orderId(),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((details) => {
          this.data.set(details);
        }),
        blank(),
      );
  }
}
